/**
 * Estimativa de entrega na PDP — região via IP + prazo de personalização.
 * No checkout, o prazo exato vem do CEP (Envio Express).
 */
(function (global) {
  'use strict';

  var CACHE_KEY = 'vestfirma_pdp_geo_v1';
  var CACHE_TTL_MS = 24 * 60 * 60 * 1000;

  /** Dias úteis de transporte a partir de SC — origem Vestfirma. */
  var TRANSIT_BY_UF = {
    SC: 2,
    RS: 3,
    PR: 3,
    SP: 4,
    RJ: 5,
    MG: 5,
    ES: 5,
    GO: 6,
    DF: 6,
    MS: 7,
    MT: 7,
    BA: 8,
    SE: 9,
    AL: 9,
    PE: 9,
    PB: 9,
    RN: 9,
    CE: 9,
    PI: 10,
    MA: 10,
    PA: 10,
    AM: 11,
    AP: 11,
    RR: 11,
    RO: 10,
    AC: 11,
    TO: 9,
  };

  function readCache() {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || !parsed.at || Date.now() - parsed.at > CACHE_TTL_MS) return null;
      return parsed.data || null;
    } catch (err) {
      return null;
    }
  }

  function writeCache(data) {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), data: data }));
    } catch (err) {
      /* ok */
    }
  }

  function normalizeUf(value) {
    return String(value || '')
      .trim()
      .toUpperCase()
      .slice(0, 2);
  }

  function transitDaysForUf(uf) {
    if (!uf) return 0;
    return TRANSIT_BY_UF[uf] != null ? TRANSIT_BY_UF[uf] : 8;
  }

  function parsePayload(payload) {
    if (!payload || typeof payload !== 'object') return null;
    var country = normalizeUf(payload.country_code || payload.countryCode || payload.country);
    if (country && country !== 'BR') {
      return { country: country, uf: '', isBrazil: false };
    }
    var uf = normalizeUf(payload.region_code || payload.regionCode || payload.region);
    return { country: 'BR', uf: uf, isBrazil: true };
  }

  function fetchGeo() {
    var cached = readCache();
    if (cached) return Promise.resolve(cached);

    return fetch('https://ipwho.is/', { credentials: 'omit' })
      .then(function (res) {
        if (!res.ok) throw new Error('geo');
        return res.json();
      })
      .then(function (json) {
        if (json && json.success === false) throw new Error('geo');
        var geo = parsePayload(json);
        if (geo) writeCache(geo);
        return geo;
      })
      .catch(function () {
        return fetch('https://ipapi.co/json/', { credentials: 'omit' })
          .then(function (res) {
            if (!res.ok) throw new Error('geo');
            return res.json();
          })
          .then(function (json) {
            var geo = parsePayload(json);
            if (geo) writeCache(geo);
            return geo;
          });
      })
      .catch(function () {
        return null;
      });
  }

  function scheduleGeoFetch(callback) {
    var run = function () {
      fetchGeo().then(callback);
    };
    if (typeof global.requestIdleCallback === 'function') {
      global.requestIdleCallback(run, { timeout: 2500 });
    } else {
      global.setTimeout(run, 1200);
    }
  }

  function isProntaEntregaActive(el) {
    var pdp = el.closest('[data-md-pdp]');
    if (!pdp) return false;
    var meta = pdp.querySelector('[data-md-stock]');
    if (!meta || !meta.hasAttribute('data-production-full')) return false;
    return meta.classList.contains('md-pdp__meta--in-stock');
  }

  function fallbackTotalDays(el) {
    var productionDays = Number(el.dataset.productionDays) || 4;
    var fallbackShippingDays = Number(el.dataset.fallbackShippingDays) || 5;
    var prontaDispatchDays = Number(el.dataset.prontaEntregaDispatchDays) || 2;

    if (el.dataset.prontaEntrega === 'true' && isProntaEntregaActive(el)) {
      return prontaDispatchDays + fallbackShippingDays;
    }

    return productionDays + fallbackShippingDays;
  }

  function isGeoResolved(geo) {
    return !!(geo && geo.isBrazil && geo.uf);
  }

  function computeTotalDays(el, geo) {
    if (!isGeoResolved(geo)) {
      return fallbackTotalDays(el);
    }

    var productionDays = Number(el.dataset.productionDays) || 0;
    var dispatchDays = Number(el.dataset.dispatchDays) || 1;
    var prontaDispatchDays = Number(el.dataset.prontaEntregaDispatchDays) || 2;

    if (el.dataset.prontaEntrega === 'true' && isProntaEntregaActive(el)) {
      return prontaDispatchDays + transitDaysForUf(geo.uf);
    }

    return productionDays + dispatchDays + transitDaysForUf(geo.uf);
  }

  function formatMessage(el, days) {
    var tpl = el.dataset.templateEstimate || 'Estimativa de entrega: até __DAYS__ dias úteis.';
    return tpl.replace(/__DAYS__/g, String(days));
  }

  function render(el, geo) {
    var days = computeTotalDays(el, geo);
    var textEl = el.querySelector('[data-md-delivery-estimate-text]');
    if (textEl) textEl.textContent = formatMessage(el, days);
    el.hidden = false;
    el.classList.add('is-ready');
    el.removeAttribute('aria-busy');
  }

  function initEl(el) {
    if (el.dataset.mdDeliveryEstimateInit === 'true') return;
    el.dataset.mdDeliveryEstimateInit = 'true';

    var pdp = el.closest('[data-md-pdp]');
    var stockMeta = pdp ? pdp.querySelector('[data-md-stock]') : null;
    var cached = readCache();

    function refreshFromCacheOrFallback() {
      render(el, cached || null);
    }

    refreshFromCacheOrFallback();

    if (!cached) {
      scheduleGeoFetch(function (geo) {
        if (geo) {
          cached = geo;
          render(el, geo);
        }
      });
    }

    if (stockMeta && typeof MutationObserver !== 'undefined') {
      var observer = new MutationObserver(refreshFromCacheOrFallback);
      observer.observe(stockMeta, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }
  }

  function initRoot(root) {
    if (!root) return;
    root.querySelectorAll('[data-md-delivery-estimate]').forEach(initEl);
  }

  function init() {
    initRoot(document);
  }

  global.VestfirmaPdpDeliveryEstimate = {
    init: init,
    initRoot: initRoot,
    transitDaysForUf: transitDaysForUf,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})(window);
